import type { ReactElement } from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import type { UserRole } from '../../domain/types';
import { canReadAssistant, canViewInventoryAndTransfers, canViewProcurementModules } from '../../domain/formatters';

function linkClass(isActive: boolean): string {
  return isActive ? 'app-main-nav-link app-main-nav-link--active' : 'app-main-nav-link';
}

export function MainNav({
  role,
  sourcingEnabled = true,
  materialTransfersEnabled = true,
  permissions
}: {
  role: UserRole;
  /** When false, hide RFQ / request-for-quotes navigation. */
  sourcingEnabled?: boolean;
  /** When false, hide Inventory and Material transfer navigation. */
  materialTransfersEnabled?: boolean;
  permissions?: string[];
}): ReactElement {
  const { pathname } = useLocation();
  const procurement = canViewProcurementModules(role);
  const inv = canViewInventoryAndTransfers(role);

  const requestsActive =
    pathname === '/requests' || pathname.startsWith('/requests/');
  const poActive = pathname === '/purchase-orders' || pathname.startsWith('/purchase-orders/');
  const rfqActive = pathname === '/request-for-quotes' || pathname.startsWith('/request-for-quotes/');
  const assistantActive = pathname === '/assistant' || pathname.startsWith('/assistant/');
  const wellsActive = pathname === '/wells';
  const showAssistant = canReadAssistant(role, permissions);
  const vendorsActive = pathname === '/vendors';
  const invActive = Boolean(matchPath({ path: '/inventory', end: true }, pathname));
  const mtActive = Boolean(
    matchPath({ path: '/material-transfers', end: true }, pathname) ||
    matchPath({ path: '/material-transfers/new', end: true }, pathname) ||
    matchPath({ path: '/material-transfers/:transferId/edit', end: true }, pathname)
  );

  return (
    <nav className="app-main-nav" aria-label="Main">
      <Link to="/" className={linkClass(false)}>
        Modules
      </Link>
      {procurement && (
        <Link to="/search" className={linkClass(pathname === '/search')} data-testid="nav-search">
          Search
        </Link>
      )}
      <Link to="/requests" className={linkClass(requestsActive)}>
        Requests
      </Link>
      {inv && materialTransfersEnabled && (
        <>
          <Link to="/inventory" className={linkClass(invActive)}>
            Inventory
          </Link>
          <Link to="/material-transfers" className={linkClass(mtActive)}>
            Material Transfer
          </Link>
        </>
      )}
      {procurement && (
        <>
          <Link to="/purchase-orders" className={linkClass(poActive)}>
            Purchase orders
          </Link>
          {sourcingEnabled && (
            <Link to="/request-for-quotes" className={linkClass(rfqActive)}>
              Request for quotes
            </Link>
          )}
          {showAssistant && (
            <Link to="/assistant" className={linkClass(assistantActive)} data-testid="nav-assistant">
              Assistant
            </Link>
          )}
          <Link to="/wells" className={linkClass(wellsActive)}>
            Wells
          </Link>
          <Link to="/vendors" className={linkClass(vendorsActive)}>
            Vendors
          </Link>
        </>
      )}
    </nav>
  );
}
